import React, { useEffect, useState } from 'react';
import { admin } from '../services/api';
import { toast } from '../hooks/use-toast';
import { ArrowUp, ArrowDown, GripVertical, Trash2, Plus, RefreshCw, Loader2, Rss, AlertTriangle } from 'lucide-react';

const EMPTY = { name: '', feedUrl: '', siteUrl: '' };
const errMsg = (e) => e?.response?.data?.detail || 'Try again';

export default function AdminNewsSourcesPanel() {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState('');
  const [dragId, setDragId] = useState(null);

  const load = () => admin.newsSources().then(setSources);
  useEffect(() => { load(); }, []);

  const saveOrder = async (list) => {
    setSources(list);
    try { await admin.reorderNewsSources(list.map(s => s.id)); } catch (e) { toast({ title: 'Reorder failed', description: errMsg(e) }); load(); }
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= sources.length) return;
    const list = [...sources];
    [list[i], list[j]] = [list[j], list[i]];
    saveOrder(list);
  };
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const list = sources.filter(s => s.id !== dragId);
    list.splice(list.findIndex(s => s.id === targetId), 0, sources.find(s => s.id === dragId));
    setDragId(null);
    saveOrder(list);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy('add');
    try {
      const s = await admin.addNewsSource({ ...form, siteUrl: form.siteUrl || null });
      toast({ title: 'Source added', description: s.lastError ? `Saved, but fetch failed: ${s.lastError}` : `${s.lastCount || 0} articles pulled` });
      setForm(EMPTY); load();
    } catch (e2) { toast({ title: 'Could not add source', description: errMsg(e2) }); }
    setBusy('');
  };
  const toggle = async (s) => {
    await admin.updateNewsSource(s.id, { name: s.name, feedUrl: s.feedUrl, siteUrl: s.siteUrl, enabled: !s.enabled });
    load();
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}" and its articles?`)) return;
    await admin.deleteNewsSource(s.id); load();
  };
  const refresh = async () => {
    setBusy('refresh');
    try { const r = await admin.refreshNewsSources(); toast({ title: 'Feed refreshed', description: `${r.articles} articles from ${r.sources} sources` }); load(); }
    catch (e) { toast({ title: 'Refresh failed', description: errMsg(e) }); }
    setBusy('');
  };

  return (
    <div className="pbb-card p-5 space-y-4" data-testid="admin-news-sources">
      <div className="flex items-center gap-3 flex-wrap">
        <Rss className="text-green-400" size={18}/>
        <div className="font-semibold">RSS News Sources</div>
        <span className="text-xs text-gray-500">Auto-refreshes every 30 minutes · drag or use arrows to reorder</span>
        <button onClick={refresh} disabled={busy === 'refresh'} data-testid="news-sources-refresh"
          className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-green-600/40 text-green-400 text-xs hover:bg-green-600/10">
          {busy === 'refresh' ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Refresh now
        </button>
      </div>

      <ul className="divide-y divide-[#1f1f1f]" data-testid="news-sources-list">
        {sources.map((s, i) => (
          <li key={s.id} draggable onDragStart={() => setDragId(s.id)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(s.id)}
            data-testid={`news-source-${s.id}`}
            className={`flex items-center gap-3 py-2.5 ${dragId === s.id ? 'opacity-40' : ''} ${s.enabled ? '' : 'opacity-60'}`}>
            <GripVertical size={14} className="text-gray-600 cursor-grab shrink-0"/>
            <span className="text-xs text-gray-500 w-5">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-200">{s.name}</div>
              <div className="text-[11px] text-gray-500 truncate">{s.feedUrl}</div>
              {s.lastError
                ? <div className="text-[11px] text-red-400 inline-flex items-center gap-1"><AlertTriangle size={10}/> {s.lastError.slice(0, 90)}</div>
                : s.lastFetchedAt && <div className="text-[11px] text-gray-500">{s.lastCount ?? 0} articles · {new Date(s.lastFetchedAt).toLocaleString()}</div>}
            </div>
            <button onClick={() => toggle(s)} data-testid={`news-source-toggle-${i}`}
              className={`text-[11px] px-2 py-0.5 rounded-full border ${s.enabled ? 'border-green-600/40 text-green-400' : 'border-gray-600 text-gray-400'}`}>
              {s.enabled ? 'On' : 'Off'}
            </button>
            <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`news-source-up-${i}`} className="p-1 text-gray-400 hover:text-white disabled:opacity-20"><ArrowUp size={14}/></button>
            <button onClick={() => move(i, 1)} disabled={i === sources.length - 1} data-testid={`news-source-down-${i}`} className="p-1 text-gray-400 hover:text-white disabled:opacity-20"><ArrowDown size={14}/></button>
            <button onClick={() => remove(s)} data-testid={`news-source-delete-${i}`} className="p-1 text-gray-500 hover:text-red-400"><Trash2 size={14}/></button>
          </li>
        ))}
      </ul>

      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1.5fr_auto] gap-2" data-testid="news-source-form">
        <input required className="pbb-input" placeholder="Source name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="news-source-name"/>
        <input required type="url" className="pbb-input" placeholder="RSS feed URL (https://…/feed)" value={form.feedUrl} onChange={e => setForm({ ...form, feedUrl: e.target.value })} data-testid="news-source-feed-url"/>
        <input type="url" className="pbb-input" placeholder="Website (optional)" value={form.siteUrl} onChange={e => setForm({ ...form, siteUrl: e.target.value })} data-testid="news-source-site-url"/>
        <button disabled={busy === 'add'} data-testid="news-source-add"
          className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">
          {busy === 'add' ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Add
        </button>
      </form>
    </div>
  );
}
